'use client'

import { useEffect } from 'react'

function isBenignBrowserNoise(message: string): boolean {
  // WebKit NullMedia EmptyRanges (STYTRIX-WEB-A) — engine bug, not app code.
  // See https://bugs.webkit.org/show_bug.cgi?id=318284
  if (message.includes('EmptyRanges')) return true
  // next-themes 0.4.6 renders its FOUC-prevention <script> inside ThemeProvider;
  // React 19.2 dev logs this on every client render. Upstream fix (PR #386) is
  // unmerged and the package is unmaintained. Dev-only console noise, no runtime
  // effect. See https://github.com/pacocoursey/next-themes/issues/387
  if (message.includes('Encountered a script tag while rendering React component')) return true
  // Existing Clerk / Next noise
  if (
    message.includes('requestAsyncStorage') ||
    message.includes('cookies() expects to have') ||
    message.includes('invalidateCacheAction') ||
    message.includes('Invariant:')
  ) {
    return true
  }
  return false
}

export default function ErrorSuppressor() {
  useEffect(() => {
    // Suppress Clerk's requestAsyncStorage error in console
    const originalError = console.error
    console.error = (...args) => {
      const errorMessage = args[0]?.toString() || ''

      if (isBenignBrowserNoise(errorMessage)) {
        return
      }

      // Log all other errors normally
      originalError.apply(console, args)
    }

    // Suppress unhandled promise rejections from Clerk / WebKit noise
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const errorMessage = event.reason?.message || event.reason?.toString() || ''

      if (isBenignBrowserNoise(errorMessage)) {
        event.preventDefault()
        return
      }
    }

    // Suppress runtime errors from Clerk / WebKit EmptyRanges
    const handleError = (event: ErrorEvent) => {
      const errorMessage = event.message || event.error?.message || ''

      if (isBenignBrowserNoise(errorMessage)) {
        event.preventDefault()
        return
      }
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    window.addEventListener('error', handleError)

    return () => {
      console.error = originalError
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      window.removeEventListener('error', handleError)
    }
  }, [])

  return null
}
